import { RoundButton } from "@/components/buttons/Buttons";
import CardContainer from "@/components/cards/CardContainer";
import Container, { Column, Row } from "@/components/container/Container";
import Spacing from "@/components/container/Spacing";
import { TextBodyMedium, TextHeadlineExtraSmall, TextLabelLarge } from "@/components/text/Texts";
import { Slider } from "primereact/slider";
import { classNames } from "primereact/utils";
import { JSX, useContext } from "react";
import { TripData, TripDriverState, TripType, getActivityCardInfoType, X4bActivitySection } from "../engine/ConciergeCardViewModel";
import { CardContext } from "../context/CardContext";
import clsx from "clsx";
import { getVehicleTypeInfo, XpressVehicleType } from "@/modules/vehicle/engine/VehicleType";
import { getVehicleColorStyle } from "@/modules/vehicle/engine/ActivityTypes";
import { cn } from "@/components/ui/cn";
import { ActivityHelper, X4bActivityDto } from "@/modules/vehicle/engine/X4bActivityDto";
import { X4bActivityStatus } from "@/services/signalr.service";

// What kind of synthetic/internal/invisible entity is this? Don't we have enough statuses and labels to decide what to draw?
export enum CardInfoType {
	PickupDestination = 1,
	VehicleInfo,
	TripInfo,
	VehicleTripInfo,
}

export interface ConciergeCardProps {
	tripInfo: TripData;
	button?: JSX.Element;
	invalidateParent?: Function;
}
// What kind of synthetic/internal/invisible entity is this? Don't we have enough statuses and labels to decide what to draw?
class CardSliderSettings {
	isPresent: boolean = false;
	isActive: boolean = false;
	text: string = '';
	progress: number = 0;
	color: string = '';
	vehicle: XpressVehicleType = XpressVehicleType.Sedan;
}

// TODO[Phase2]: refactor this ball of spaghetties. Make separate view for every card type.
const ConciergeCard = ({ tripInfo, button }: ConciergeCardProps) => {
	const { selectedCard, setSelectedCard } = useContext(CardContext);
	const isSelected = selectedCard?.id === tripInfo.id;

	let infoType = getActivityCardInfoType(tripInfo);

	if (infoType! === undefined) throw Error('ConciergeCard infoType is undefined')

	let infoColumn = (<></>);
	if (infoType === CardInfoType.PickupDestination) {
		infoColumn = (
			<Row className="gap-[4px] w-1/2">
				<img src="/images/concierge-pickup-destination.svg" alt="pickup destination" />

				<Column className="justify-between w-[calc(100%-(20px+4px))]"> {/* image size + gap */}
					<TextBodyMedium
						text={tripInfo.pickup.name}
						className="whitespace-nowrap truncate text-primary50 -mt-[1px]"
					/>
					<TextBodyMedium
						text={tripInfo.destination.name}
						className="whitespace-nowrap truncate text-primary50 -mb-[3px]"
					/>
				</Column>
			</Row>
		);
	} else if (infoType === CardInfoType.VehicleInfo || infoType === CardInfoType.VehicleTripInfo) {
		const vehicleIconSrc = (tripInfo.vehicle.type === XpressVehicleType.Sedan || tripInfo.vehicle.type === XpressVehicleType.SUV)
			? '/images/directions_car_filled.svg'
			: '/images/two_wheeler.svg';
		const tripTypeIconSrc = tripInfo.type === TripType.OneWay
			? '/images/oneway.svg'
			: '/images/roundtrip.svg';
		const tripTypeStr = tripInfo.type === TripType.OneWay
			? 'One-way'
			: 'Round-Trip';

		infoColumn = (
			<Column className={`w-1/2 ${infoType === CardInfoType.VehicleInfo ? 'gap-[24px]' : ''}`}>
				<Row className="gap-[10px]">
					<img src={vehicleIconSrc} alt="pickup destination" />
					<TextBodyMedium
						text={tripInfo.vehicle.plate}
						className="whitespace-nowrap truncate"
					/>
				</Row>
				<Row className="gap-[10px]">
					{/* This div purpose is clearly only to render vehicle color. Why on Earth it has condition based on CardInfoType.VehicleInfo?*/}
					<div className={classNames('w-[24px]', 'h-[24px]')}
						style={{ backgroundColor: `${infoType === CardInfoType.VehicleInfo && getVehicleColorStyle(tripInfo.vehicle.vehicleColor)}` }}
					/>
					<TextBodyMedium
						text={tripInfo.vehicle.vehicle}
						className="whitespace-nowrap truncate"
					/>
				</Row>
				{infoType === CardInfoType.VehicleTripInfo && (
					<Row className="gap-[10px]">
						<img src={tripTypeIconSrc} alt="trip type" />
						<TextBodyMedium
							text={tripTypeStr}
							className="whitespace-nowrap truncate"
						/>
					</Row>
				)}
			</Column>
		);
	} else if (infoType === CardInfoType.TripInfo) {
		const tripTypeIconSrc = tripInfo.type === TripType.OneWay
			? '/images/oneway.svg'
			: '/images/roundtrip.svg';
		const tripTypeStr = tripInfo.type === TripType.OneWay
			? 'One-way'
			: 'Round-Trip';
		const tripExtraIconSrc = tripInfo.tripDriverState === TripDriverState.DriverCancelled || tripInfo.needsAttentionIsRebookRide
			? '/images/error.svg'
			: '/images/search.svg'
		const tripExtraStr = tripInfo.tripDriverState === TripDriverState.DriverCancelled || tripInfo.needsAttentionIsRebookRide
			? 'Driver Cancelled'
			: 'Finding a driver';

		infoColumn = (
			<Column className="w-1/2">
				{tripInfo.tripDriverState !== TripDriverState.None && (
					<Row className="gap-[10px]">
						<img src={tripExtraIconSrc} alt="extra" />
						<TextBodyMedium
							text={tripExtraStr}
							className="whitespace-nowrap truncate"
						/>
					</Row>
				)}
				<Row className="gap-[10px]">
					<img src={tripTypeIconSrc} alt="trip type" />
					<TextBodyMedium
						text={tripTypeStr}
						className="whitespace-nowrap truncate"
					/>
				</Row>
			</Column>
		);
	}

	const sliderSettings = new CardSliderSettings();
	const status = tripInfo.act.Status;
	if (status === undefined) {
	}
	else if (ActivityHelper.isScheduled(tripInfo.act)) {
		sliderSettings.isPresent = false;
		// Cancel button doesn't exist in figma either but I don't think it is correct.
	}
	else if ([X4bActivityStatus.New,
	X4bActivityStatus.RiderAssigning].includes(status)) {
		sliderSettings.isPresent = true;
		sliderSettings.text = 'Requesting';
	} else if ([X4bActivityStatus.RiderAssigned,
	X4bActivityStatus.RiderAccept,
	X4bActivityStatus.RiderAcceptB2B,
	X4bActivityStatus.ArrivedToPickup].includes(status)) {
		sliderSettings.isPresent = true;
		sliderSettings.isActive = true;
		sliderSettings.text = tripInfo.statusText;
		sliderSettings.progress = tripInfo.progress;
		sliderSettings.vehicle = tripInfo.vehicle.type;
		sliderSettings.color = 'bg-accent60';
	} else if ([X4bActivityStatus.DepartedToPickup,
	X4bActivityStatus.PickedUp,
	X4bActivityStatus.ArrivedToDrop].includes(status)) {
		sliderSettings.isPresent = true;
		sliderSettings.isActive = true;
		sliderSettings.text = tripInfo.statusText;
		sliderSettings.progress = tripInfo.progress;
		sliderSettings.vehicle = tripInfo.vehicle.type;
		sliderSettings.color = 'bg-error60';
	}
	// else if (tripInfo.activityLabel === X4bActivityLabel.NeedsAttention && tripInfo.needsAttentionIsContactSupport) {
	// 	sliderSettings.isPresent = true;
	// 	sliderSettings.isActive = true;
	// 	sliderSettings.text = tripInfo.statusText;
	// 	sliderSettings.progress = tripInfo.progress;
	// 	sliderSettings.vehicle = tripInfo.vehicle.type;
	// 	sliderSettings.color = 'bg-error60';
	// }


	let slider;
	if (sliderSettings.isPresent) {
		slider = (
			<Column className="gap-[12px]">
				<TextBodyMedium
					text={sliderSettings.text}
					className="text-primary30"
				/>
				<Column className="relative h-3 justify-center">
					<Slider
						disabled
						min={0}
						max={100}
						value={sliderSettings.isActive ? sliderSettings.progress : undefined}
						pt={{
							root: { className: "rounded-lg opacity-100 h-[4px] bg-primary90" },
							range: { className: classNames('rounded-lg', sliderSettings.color) },
							handle: {
								className: "bg-transparent -ml-[28px] w-[55px]",
								children: (
									sliderSettings.isActive && (
										<img

											className={cn("w-full h-[24px]", getVehicleTypeInfo(sliderSettings.vehicle)?.topViewClass)}
											src={getVehicleTypeInfo(sliderSettings.vehicle)?.topViewPath}
											alt="vehicle top view"
										/>
									)
								),
							},
						}}
					/>
					{sliderSettings.isActive && (
						<div className={classNames('absolute', 'top-1/2', 'right-0', '-translate-y-1/2', 'w-1', 'h-1', 'rounded-full', sliderSettings.color)} />
					)}
				</Column>
			</Column>
		)
	}

	const hasPeople = tripInfo.additionalPassengers.length > 0;
	const personNameEl = (
		<TextHeadlineExtraSmall
			text={tripInfo.passenger.name}
			className={classNames('whitespace-nowrap', 'truncate', ActivityHelper.isRequesting(tripInfo.act) ? 'text-primary50' : 'text-primary60')}
		/>
	);

	let backgroundColor = '';
	let border = '';
	if (isSelected) {
		backgroundColor = 'bg-primary99';

		if (tripInfo.activitySection === X4bActivitySection.NeedsAttention) {
			if (tripInfo.needsAttentionIsRebookRide) {
				backgroundColor = 'bg-warning100';
				border = 'outline outline-4 outline-warning-container';
			} else if (tripInfo.needsAttentionIsContactSupport) {
				backgroundColor = 'bg-error100';
				border = 'outline outline-4 outline-error60';
			}
		}
	} else {
		backgroundColor = 'bg-white';

		if (tripInfo.activitySection === X4bActivitySection.NeedsAttention) {
			if (tripInfo.needsAttentionIsRebookRide) {
				backgroundColor = 'bg-warning100';
				border = 'outline outline-1 outline-warning-container';
			} else if (tripInfo.needsAttentionIsContactSupport) {
				backgroundColor = 'bg-error100';
				border = 'outline outline-1 outline-error60';
			}
		}
	}
	// "transition-duration: 500ms, 200ms;"

	return (
		<CardContainer
			rootClassName={clsx('transition-[background-color,outline-width] ease-linear rounded-xl mx-1', backgroundColor, border)}
			style={{ transitionDuration: '300ms, 100ms' }}
			onClick={() => setSelectedCard(tripInfo)}
		>
			<Column className="p-[16px]">
				<Row className="gap-[12px]">
					<Column className="w-1/2">
						{hasPeople && (
							<Row>
								{personNameEl}
								<Spacing width="w-2" />
								<img src="/images/people.svg" alt="people" />
								<TextLabelLarge
									text={tripInfo.additionalPassengers.length.toString()}
									className="mt-[2px] text-primary"
								/>
							</Row>
						)}
						{hasPeople || personNameEl}
						<TextBodyMedium
							text={tripInfo.passenger.phone}
							className="whitespace-nowrap truncate text-primary30"
						/>
					</Column>
					{infoColumn}
				</Row>

				{tripInfo.extraText && (<Spacing height="h-[32px]" />)}
				{tripInfo.extraText && (
					<TextBodyMedium
						text={tripInfo.extraText}
						className={clsx('whitespace-nowrap truncate', tripInfo.extraTextColor || 'text-primary30')}
					/>
				)}

				{slider && (<Spacing height="h-[32px]" />)}
				{slider && slider}

				{button && (<Spacing height="h-[16px]" />)}
				{button && button}
			</Column>
		</CardContainer>
	)
};

export default ConciergeCard;

